import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader, removeToken } from '../../utils/auth';
import StatusBadge from '../../components/admin/StatusBadge';
import { Download, Eye, LogOut, Filter } from 'lucide-react';
import '../../styles/admin/AdminDashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', service: '' });
  const [stats, setStats] = useState({ total: 0, pending: 0, contacted: 0, completed: 0 });

  useEffect(() => {
    fetchSubmissions();
  }, [filters]);

  const fetchSubmissions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.service) params.append('service', filters.service);

      const response = await axios.get(`${API_URL}/api/admin/submissions?${params}`, {
        headers: getAuthHeader()
      });

      setSubmissions(response.data.submissions);
      calculateStats(response.data.submissions);
    } catch (error) {
      if (error.response?.status === 401) {
        removeToken();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    setStats({
      total: data.length,
      pending: data.filter(s => s.status === 'En attente').length,
      contacted: data.filter(s => s.status === 'Contacté').length,
      completed: data.filter(s => s.status === 'Complété').length
    });
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/export?format=${format}`, {
        headers: getAuthHeader()
      });

      if (format === 'csv') {
        const blob = new Blob([response.data.content], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.filename;
        a.click();
      }
    } catch (error) {
      alert('Erreur lors de l\'export');
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/admin');
  };

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <div>
            <h1>Dashboard Administrateur</h1>
            <p>Naturellement Vert Fertilisation</p>
          </div>
          <div style={{display: 'flex', gap: '1rem', alignItems: 'center'}}>
            <Link to="/admin/erp" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#ECFF25',
              textDecoration: 'none',
              padding: '0.5rem 1rem',
              border: '2px solid #ECFF25',
              borderRadius: '8px',
              fontWeight: '600',
              transition: 'all 0.3s'
            }}>
              <svg width="24" height="24" viewBox="0 0 200 200" fill="none">
                <path d="M90 120 L90 180 L110 180 L110 120 Z" fill="#ECFF25" opacity="0.9"/>
                <path d="M90 120 Q60 110 50 80 Q45 60 55 40 Q70 20 90 30" stroke="#ECFF25" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.85"/>
                <path d="M110 120 L130 100 L140 80 L150 60" stroke="#ECFF25" strokeWidth="3" strokeLinecap="round" opacity="0.85"/>
                <circle cx="140" cy="80" r="3" fill="#ECFF25"/>
                <circle cx="150" cy="60" r="3" fill="#ECFF25"/>
                <circle cx="50" cy="80" r="3" fill="#ECFF25"/>
                <circle cx="55" cy="40" r="3" fill="#ECFF25"/>
                <path d="M90 30 Q100 20 110 30" stroke="#ECFF25" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="100" cy="25" r="4" fill="#ECFF25"/>
              </svg>
              Arborescence
            </Link>
            <button onClick={handleLogout} className="admin-logout-btn">
              <LogOut size={18} />
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* Stats Cards */}
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total</div>
          </div>
          <div className="stat-card stat-pending">
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">En attente</div>
          </div>
          <div className="stat-card stat-contacted">
            <div className="stat-value">{stats.contacted}</div>
            <div className="stat-label">Contactés</div>
          </div>
          <div className="stat-card stat-completed">
            <div className="stat-value">{stats.completed}</div>
            <div className="stat-label">Complétés</div>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="admin-toolbar">
          <div className="admin-filters">
            <Filter size={18} />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="admin-filter-select"
            >
              <option value="">Tous les statuts</option>
              <option value="En attente">En attente</option>
              <option value="Contacté">Contacté</option>
              <option value="Soumission envoyée">Soumission envoyée</option>
              <option value="Complété">Complété</option>
            </select>

            <select
              value={filters.service}
              onChange={(e) => setFilters({ ...filters, service: e.target.value })}
              className="admin-filter-select"
            >
              <option value="">Tous les services</option>
              <option value="Fertilisation naturelle">Fertilisation naturelle</option>
              <option value="Fertilisation et contrôle de mauvaises herbes">Fertilisation et contrôle</option>
              <option value="Taille de haie">Taille de haie</option>
              <option value="Surensemencement">Surensemencement</option>
            </select>
          </div>

          <div className="admin-actions">
            <button onClick={() => handleExport('csv')} className="admin-export-btn">
              <Download size={18} />
              Export CSV
            </button>
            <button onClick={() => handleExport('json')} className="admin-export-btn">
              <Download size={18} />
              Export JSON
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Adresse</th>
                <th>Services</th>
                <th>Superficie</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submissions.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    Aucune soumission trouvée
                  </td>
                </tr>
              ) : (
                submissions.map((submission) => (
                  <tr key={submission.id}>
                    <td>{new Date(submission.date).toLocaleDateString('fr-CA')}</td>
                    <td>
                      <strong>{submission.prenom} {submission.nom}</strong>
                      <br />
                      <small>{submission.courriel}</small>
                    </td>
                    <td>{submission.adresse}</td>
                    <td>
                      <small>{submission.services.slice(0, 2).join(', ')}</small>
                      {submission.services.length > 2 && <small>...</small>}
                    </td>
                    <td>{submission.propertyArea?.toLocaleString() || 'N/A'} pi²</td>
                    <td>
                      <StatusBadge status={submission.status} />
                    </td>
                    <td>
                      <Link to={`/admin/submissions/${submission.id}`} className="admin-view-btn">
                        <Eye size={16} />
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
